import { NavLink, useNavigate } from "react-router-dom";
import { useState, useContext } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { SearchContext } from "../context/SearchContext";
import {
  ShoppingCart,
  User,
  Menu,
  X,
  ChevronDown,
  Heart,
  Settings,
  LogOut,
  LayoutDashboard,
  PackageSearch,
  IdCard,
} from "lucide-react";

function Navbar() {
  const { allData, loading } = useContext(SearchContext);
  const [query, setQuery] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false);
  const { user: signedInUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleSearch = (e) => {
    const value = e.target.value;
    setQuery(value);

    if (value.trim() === "") {
      setFilteredResults([]);
      setShowResults(false);
      return;
    }

    const results = allData.filter((item) => {
      const text = (item.name || item.title || "").toLowerCase();
      return text.includes(value.toLowerCase());
    });

    setFilteredResults(results);
    setShowResults(true);
  };

  const handleSelectResult = (result) => {
    setQuery("");
    setShowResults(false);

    // If it's a blog → go directly to the blog page
    if (result.itemType === "blog") {
      navigate(`/blog/${result.slug}`);
      return;
    }

    // If it's a product → go to its category page with the searched product ID
    if (result.itemType === "product") {
      const category = result.category?.toLowerCase();

      let targetPath = "/shop"; // Default if no specific category
      if (category === "vip") {
        targetPath = "/vip";
      } else if (category === "flowers" || category === "flower") {
        targetPath = "/flowers";
      } else if (category === "accessories" || category === "accessory") {
        targetPath = "/accessories";
      }

      // Navigate and pass the searched product ID
      navigate(targetPath, {
        state: { searchedProductId: result._id },
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/signin");
  };

  const linkClass = ({ isActive }) =>
    `relative group transition-all duration-300 ease-in-out ${
      isActive ? "text-blue-600 font-semibold" : "text-gray-700"
    }`;

  const navItem = (label) => (
    <span className="inline-block">
      {label}
      <span className="absolute left-0 -bottom-0.5 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
    </span>
  );

  const { cartCount } = useCart();

  return (
    <header className="sticky top-0 bg-white shadow z-50 print:hidden">
      {/* Search Bar - Desktop */}
      <div className="hidden md:block bg-white border-b border-gray-200 relative">
        <div className="max-w-6xl mx-auto px-4 py-3 relative">
          <input
            type="text"
            placeholder={loading ? "Loading search data..." : "Search our site..."}
            value={query}
            onChange={handleSearch}
            disabled={loading}
            className="w-full px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          {/* Search Dropdown */}
          {showResults && (
            <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
              {filteredResults.length > 0 ? (
                filteredResults.map((result) => (
                  <button
                    key={result._id || result.slug}
                    onClick={() => handleSelectResult(result)}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    {result.name || result.title}
                  </button>
                ))
              ) : (
                <p className="px-4 py-2 text-gray-500">No results found.</p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
        {/* Logo */}
        <NavLink to="/" className="inline-block">
          <img
            src="/images/logos/Exudee-logo.svg"
            alt="Exudee Hampers Logo"
            className="h-12 w-auto"
          />
        </NavLink>

        {/* Desktop Nav */}
        <nav className="hidden md:flex space-x-10 font-medium text-gray-700 items-center relative">
          <NavLink to="/" className={linkClass}>{navItem("Home")}</NavLink>

          <div className="relative group">
            <div className="flex items-center gap-2 cursor-pointer">
              <NavLink to="/shop" className={linkClass}>
                {navItem("Shop")}
              </NavLink>
              <ChevronDown className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />
            </div>

            <div className="absolute top-full left-0 mt-3 w-56 bg-white shadow-lg border border-gray-100 rounded-md py-2 opacity-0 invisible group-hover:visible group-hover:opacity-100 transition-all duration-300 z-50"
              onMouseEnter={(e) => e.currentTarget.classList.remove("invisible", "opacity-0")}
              onMouseLeave={(e) => e.currentTarget.classList.add("invisible", "opacity-0")}
            >
              <NavLink to="/build" className="block px-4 py-2 hover:bg-gray-100">Build Your Hamper</NavLink>
              <NavLink to="/flowers" className="block px-4 py-2 hover:bg-gray-100">Flowers</NavLink>
              <NavLink to="/accessories" className="block px-4 py-2 hover:bg-gray-100">Accessories</NavLink>
              <NavLink to="/vip" className="block px-4 py-2 hover:bg-gray-100">VIP Collection</NavLink>
            </div>
          </div>

          <NavLink to="/about" className={linkClass}>{navItem("About")}</NavLink>
          <NavLink to="/contact" className={linkClass}>{navItem("Contact")}</NavLink>
          <NavLink to="/faqs" className={linkClass}>{navItem("FAQs")}</NavLink>
          <NavLink to="/blog" className={linkClass}>{navItem("Blog")}</NavLink>
        </nav>

        {/* Desktop Icons */}
        <div className="hidden md:flex items-center gap-3">
          <div className="relative group">
            {/* User Icon with Sign-in Indicator */}
            <div className="w-11 h-11 rounded-full bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-600 transition flex items-center justify-center cursor-pointer relative">
              <User className="w-6 h-6" />

              {/* ✅ Green Badge */}
              {signedInUser && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 text-white text-[12px] font-bold flex items-center justify-center rounded-full">
                  ✓
                </span>
              )}
            </div>

            {/* Dropdown Menu */}
            <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded-md opacity-0 group-hover:opacity-100 group-hover:visible invisible transition-all duration-300 z-50">
              {signedInUser ? (
                <>
                  <NavLink to="/dashboard" className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 mt-2"><LayoutDashboard className="w-5 h-5" />Dashboard</NavLink>
                  <NavLink to="/dashboard/cart" className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100"><ShoppingCart className="w-5 h-5" />My Cart</NavLink>
                  <NavLink to="/dashboard/orders" className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100"><PackageSearch className="w-5 h-5" />My Orders</NavLink>
                  <NavLink to="/dashboard/wishlist" className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100"><Heart className="w-5 h-5" />Wishlist</NavLink>
                  <NavLink to="/dashboard/settings" className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100"><Settings className="w-5 h-5" />Settings</NavLink>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full text-left px-4 py-2 mb-2 text-red-600 hover:bg-gray-100"
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <NavLink to="/signin" className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100">
                    <User className="w-4 h-4" /> Sign In
                  </NavLink>
                  <NavLink to="/register" className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100">
                    <IdCard className="w-4 h-4" /> Register
                  </NavLink>
                </>
              )}
            </div>
          </div>

          {/* Cart Icon */}
          <div
            onClick={() => {
              if (!signedInUser) {
                navigate("/signin", {
                  state: {
                    from: "/cart",
                    message: "Please sign in or register to use the cart.",
                  },
                });
              } else {
                navigate("/cart");
              }
            }}
            className="relative cursor-pointer"
          >
            <div className="w-11 h-11 rounded-full bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-600 transition flex items-center justify-center">
              <ShoppingCart className="w-6 h-6" />
            </div>
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                {cartCount}
              </span>
            )}
          </div>
        </div>

        {/* Mobile Icons */}
        <div className="md:hidden flex items-center">
          <div className="relative mr-2">
            <button
              onClick={() => {
                setIsOpen(true);
                setMobileUserMenuOpen((prev) => !prev);
              }}
              className="w-9 h-9 rounded-full bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-600 transition flex items-center justify-center"
            >
              <User className="w-5 h-5" />
            </button>

            {/* ✅ Green Badge */}
            {signedInUser && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 text-white text-[12px] font-bold flex items-center justify-center rounded-full">
                ✓
              </span>
            )}
          </div>

          <div
            onClick={() => {
              if (!signedInUser) return navigate("/signin");
              navigate("/cart");
            }}
            className="relative mr-2 cursor-pointer"
          >
            <div className="w-9 h-9 rounded-full bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-600 transition flex items-center justify-center">
              <ShoppingCart className="w-5 h-5" />
            </div>
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                {cartCount}
              </span>
            )}
          </div>
          <button
            onClick={() => {
              setIsOpen((prev) => {
                const next = !prev;
                if (!next) setMobileUserMenuOpen(false); // 👈 reset user dropdown on close
                return next;
              });
            }}
            className="text-gray-700 focus:outline-none ml-2"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30"
          onClick={() => {
            setIsOpen(false);
            setMobileUserMenuOpen(false); // 👈 reset user dropdown when sidebar closes
          }}
        ></div>
      )}


      {/* Slide-in Mobile Nav */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-6 pt-6 pb-2 border-b border-gray-200">
          <NavLink to="/" onClick={() => setIsOpen(false)} className="text-xl font-bold text-blue-600">
            Exudee
          </NavLink>
        </div>

        <div className="flex flex-col px-6 pt-6 pb-6 gap-4 font-medium text-gray-700">
          <div className="md:hidden px-4 relative">
            <input
              type="text"
              placeholder={loading ? "Loading search data..." : "Search our site..."}
              value={query}
              onChange={handleSearch}
              disabled={loading}
              className="w-full px-4 py-2 mb-4 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />

            {showResults && (
              <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
                {filteredResults.length > 0 ? (
                  filteredResults.map((result) => (
                    <button
                      key={result._id}
                      onClick={() => handleSelectResult(result)}
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      {result.name || result.title}
                    </button>
                  ))
                ) : (
                  <p className="px-4 py-2 text-gray-500">No results found.</p>
                )}
              </div>
            )}
          </div>

          <NavLink to="/" className={linkClass} onClick={() => setIsOpen(false)}>{navItem("Home")}</NavLink>

          <div>
            <button
              className="flex items-center justify-between w-full text-gray-700 hover:text-blue-600 transition"
              onClick={() => setMobileShopOpen(!mobileShopOpen)}
            >
              <NavLink to="/shop" onClick={() => setIsOpen(false)} className="flex-1 text-left">
                {navItem("Shop")}
              </NavLink>
              <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-300 ${mobileShopOpen ? "rotate-180" : ""}`} />
            </button>

            {mobileShopOpen && (
              <div className="pl-4 mt-3">
                {[{ label: "Build Your Hamper", to: "/build" }, { label: "Flowers", to: "/flowers" }, { label: "Accessories", to: "/accessories" }, { label: "VIP", to: "/vip" }].map((item, index) => (
                  <NavLink
                    key={index}
                    to={item.to}
                    onClick={() => setIsOpen(false)}
                    className="block text-sm py-3 px-2 rounded hover:bg-blue-100 transition-colors duration-200"
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          <NavLink to="/about" className={linkClass} onClick={() => setIsOpen(false)}>{navItem("About")}</NavLink>
          <NavLink to="/contact" className={linkClass} onClick={() => setIsOpen(false)}>{navItem("Contact")}</NavLink>
          <NavLink to="/faqs" className={linkClass} onClick={() => setIsOpen(false)}>{navItem("FAQs")}</NavLink>
          <NavLink to="/blog" className={linkClass} onClick={() => setIsOpen(false)}>{navItem("Blog")}</NavLink>

          {mobileUserMenuOpen && (
            <div className="border-t pt-4 mt-4 space-y-3 text-sm">
              {signedInUser ? (
                <>
                  <NavLink to="/dashboard" onClick={() => { setIsOpen(false); setMobileUserMenuOpen(false); }} className="flex items-center gap-2 hover:text-blue-600">
                    <LayoutDashboard className="w-4 h-4" /> Dashboard
                  </NavLink>
                  <NavLink to="/dashboard/cart" onClick={() => { setIsOpen(false); setMobileUserMenuOpen(false); }} className="flex items-center gap-2 hover:text-blue-600">
                    <ShoppingCart className="w-4 h-4" /> My Cart
                  </NavLink>
                  <NavLink to="/dashboard/orders" onClick={() => { setIsOpen(false); setMobileUserMenuOpen(false); }} className="flex items-center gap-2 hover:text-blue-600">
                    <PackageSearch className="w-4 h-4" /> My Orders
                  </NavLink>
                  <NavLink to="/dashboard/wishlist" onClick={() => { setIsOpen(false); setMobileUserMenuOpen(false); }} className="flex items-center gap-2 hover:text-blue-600">
                    <Heart className="w-4 h-4" /> Wishlist
                  </NavLink>
                  <NavLink to="/dashboard/settings" onClick={() => { setIsOpen(false); setMobileUserMenuOpen(false); }} className="flex items-center gap-2 hover:text-blue-600">
                    <Settings className="w-4 h-4" /> Settings
                  </NavLink>
                  <button
                    onClick={() => {
                      handleLogout();
                    }}
                    className="flex items-center gap-2 text-red-600 hover:text-red-800"
                  >
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </>
              ) : (
                <>
                  <NavLink to="/signin" onClick={() => { setIsOpen(false); setMobileUserMenuOpen(false); }} className="flex items-center gap-2 hover:text-blue-600">
                    <User className="w-4 h-4" /> Sign In
                  </NavLink>
                  <NavLink to="/register" onClick={() => { setIsOpen(false); setMobileUserMenuOpen(false); }} className="flex items-center gap-2 hover:text-blue-600">
                    <IdCard className="w-4 h-4" /> Register
                  </NavLink>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Navbar;
